import { defineStore } from 'pinia';
import { api } from '../boot/axios';
import {
  prepProtocolMap,
  prepWorkflowInstance,
  prepWorkflowTemplate,
  sortInstanceSteps,
  WorkflowTemplateTypes,
} from '../logic/workflow';

export const useWorkflowStore = defineStore('workflow', {
  // See https://pinia.vuejs.org/cookbook/migration-vuex.html for details
  persist: true,
  state: () => ({
    templates: [],
    instances: [],
    triggers: [],
  }),
  getters: {
    getWorkflowTemplates: (state) => {
      return state.templates.filter((t) => t.type === WorkflowTemplateTypes.DEFAULT);
    },
    getWorkflowTemplateByGUID: (state) => (templateGUID) => {
      return state.templates.find((template) => template.GUID === templateGUID);
    },
    getWorkflowInstances: (state) => {
      return state.instances;
    },
    getWorkflowInstanceByGUID: (state) => (instanceGUID) => {
      return state.instances.find((instance) => instance.GUID === instanceGUID) || {};
    },
    getInstancesByPatientID: (state) => (patientRecordID) => {
      return (
        state.instances.filter((instance) => instance.patientRecordID == patientRecordID) || {}
      );
    },
    getProtocolMapByGUID: (state) => (protocolGUID) => {
      return state.templates.find((t) => t.GUID === protocolGUID);
    },
    getProtocolMaps: (state) => {
      return state.templates.filter((template) => template.type === WorkflowTemplateTypes.PROTOCOL);
    },
    getTriggers: (state) => {
      return state.triggers;
    },
    getTriggerByGUID: (state) => (triggerGUID) => {
      return state.triggers.find((trigger) => trigger.GUID === triggerGUID);
    },
  },
  actions: {
    fetchTemplate(templateGUID) {
      api
        .get(`/api/v2/workflows/templates/${templateGUID}`)
        .then((res) => {
          const template = res.data;
          const i = this.templates.findIndex((t) => t.GUID === template.GUID);
          if (i === -1) {
            this.templates.push(template);
          } else {
            this.templates[i] = template;
          }
        })
        .catch((err) => console.error(err));
    },
    fetchTemplates() {
      api
        .get('api/v2/workflows/templates')
        .then((res) => {
          this.templates = this.templates.filter((t) => t.type !== WorkflowTemplateTypes.DEFAULT);
          this.templates = this.templates.concat(res.data);
        })
        .catch((err) => console.error(err));
    },
    postTemplate(template) {
      api
        .post('api/v2/workflows/templates/', prepWorkflowTemplate(template))
        .then((res) => {
          this.templates.push(res.data);
        })
        .catch((err) => console.error(err));
    },
    putTemplate(template) {
      api
        .put(`api/v2/workflows/templates/${template.GUID}`, prepWorkflowTemplate(template))
        .then((res) => {
          const i = this.templates.findIndex((t) => t.GUID === res.data.GUID);
          if (i === -1) {
            this.templates.push(res.data);
          } else {
            this.templates[i] = res.data;
          }
        })
        .catch((err) => console.error(err));
    },
    fetchInstance(GUID) {
      api
        .get(`/api/v2/workflows/instances/${GUID}`)
        .then((res) => {
          const newInstance = sortInstanceSteps(res.data);
          const i = this.instances.findIndex((instance) => instance.GUID === newInstance.GUID);
          if (i === -1) {
            this.instances.push(newInstance);
          } else {
            this.instances[i] = newInstance;
          }
        })
        .catch((err) => console.error(err));
    },
    fetchPatientInstances(patientID) {
      api
        .get(`/api/v2/workflows/instances/patient/${patientID}`)
        .then((res) => {
          const instances = res.data;
          this.instances = this.instances.filter((i) => {
            if (i.patientRecordID !== patientID) {
              return true;
            } // If the patient ID doesn't match
            // if the ID matches and the GUID is in the incoming GUIDs
            return !!(
              i.patientRecordID === patientID &&
              instances.some((instance) => instance.GUID === i.GUID)
            );
            // the patient must match but the incoming instances do not contain this one.
          });
          instances.forEach((instance) => {
            const newInstance = instance;
            const i = this.instances.findIndex((instance) => instance.GUID === newInstance.GUID);
            if (i === -1) {
              this.instances.push(newInstance);
            } else {
              this.instances[i] = newInstance;
            }
          });
        })
        .catch((err) => console.error(err));
    },
    postInstance(instance) {
      api
        .post('api/v2/workflows/instances/', prepWorkflowInstance(instance))
        .then((res) => {
          const newInstance = sortInstanceSteps(res.data);
          const i = this.instances.findIndex((instance) => instance.GUID === newInstance.GUID);
          if (i === -1) {
            this.instances.push(newInstance);
          } else {
            this.instances[i] = newInstance;
          }
        })
        .catch((err) => console.error(err));
    },
    async putInstance(instance) {
      const res = await api.put(
        `api/v2/workflows/instances/${instance.GUID}`,
        prepWorkflowInstance(instance),
      );
      const newInstance = sortInstanceSteps(res.data);
      const i = this.instances.findIndex((instance) => instance.GUID === newInstance.GUID);
      if (i === -1) {
        this.instances.push(newInstance);
      } else {
        this.instances[i] = newInstance;
      }
    },
    // RFS report dates are saved apart from the checklist, so saving a checklist can never clear them.
    async fetchRFSReportDates(instanceGUID) {
      const { data } = await api.get(`api/v2/workflows/instances/${instanceGUID}/rfs-report-dates`);
      return data;
    },
    async putRFSReportDates(instanceGUID, reportDates) {
      const { data } = await api.put(
        `api/v2/workflows/instances/${instanceGUID}/rfs-report-dates`,
        reportDates,
      );
      return data;
    },
    async fetchWorkflowPDFByGUID(ID, lang) {
      return api({
        url: `api/v2/workflows/instances/${ID}/pdf/?lang=${lang}`,
        responseType: 'blob',
        method: 'GET',
      });
    },
    async fetchWorkflowPDF(instance, lang) {
      return await this.fetchWorkflowPDFByGUID(instance.GUID, lang);
    },
    async fetchTriggers() {
      try {
        const res = await api.get(`api/v2/workflows/triggers`);
        this.triggers = res.data;
      } catch (err) {
        console.error(err);
      }
    },
    fetchTrigger(triggerGUID) {
      api.get(`api/v2/workflows/triggers/${triggerGUID}`).then((res) => {
        const i = this.triggers.findIndex((trigger) => trigger.GUID === triggerGUID);
        if (i === -1) {
          this.triggers.push(res.data);
        } else {
          this.triggers[i] = res.data;
        }
      });
    },
    postTrigger(trigger) {
      api
        .post(`api/v2/workflows/triggers`, trigger)
        .then((res) => {
          const newTrigger = res.data;
          const i = this.triggers.findIndex((t) => t.GUID === newTrigger.GUID);
          if (i === -1) {
            this.triggers.push(newTrigger);
          } else {
            this.triggers[i] = newTrigger;
          }
        })
        .catch((err) => console.error(err));
    },
    activateWorkflowTrigger(stepTrigger) {
      api
        .post(`api/v2/workflows/triggers/${stepTrigger.triggerGUID}/activate`, stepTrigger)
        .then(() => {})
        .catch((err) => console.error(err));
    },
    async sendWorkflowNotification(notification) {
      try {
        await api.post(`api/v2/workflows/instances/${notification.instanceGUID}`, notification);
      } catch (e) {
        console.error(e);
      }
    },
    putTrigger(trigger) {
      api
        .put(
          `
        api / v2 / workflows / triggers /${trigger.GUID}`,
          trigger,
        )
        .then((res) => {
          const newTrigger = res.data;
          const i = this.triggers.findIndex((t) => t.GUID === newTrigger.GUID);
          if (i === -1) {
            this.triggers.push(newTrigger);
          } else {
            this.triggers[i] = newTrigger;
          }
        })
        .catch((err) => console.error(err));
    },
    deleteTrigger(trigger) {
      api
        .delete(
          `;
        api / v2 / workflows / triggers /${trigger.GUID}`,
          trigger,
        )
        .then(() => {
          this.triggers = this.triggers.filter((t) => t.GUID !== trigger.GUID);
        })
        .catch((err) => console.error(err));
    },
    async fetchProtocolMaps() {
      try {
        const res = await api.get('api/v2/protocols/protocolmaps');
        this.templates = this.templates.filter((t) => t.type !== WorkflowTemplateTypes.PROTOCOL);
        this.templates = this.templates.concat(res.data);
      } catch (err) {
        console.error(err);
      }
    },

    async fetchProtocolMap(protocolMapGUID) {
      try {
        const res = await api.get(`;
        api / v2 / protocols / protocolmaps /${protocolMapGUID}`);
        const i = this.templates.filter((t) => t.GUID === res.data.GUID);
        if (i === -1) {
          this.templates.push(res.data);
        } else {
          this.templates[i] = res.data;
        }
      } catch (err) {
        console.error(err);
      }
    },
    async postProtocolMap(protocolMap) {
      try {
        const { data } = await api.post(
          'api/v2/protocols/protocolmaps',
          prepProtocolMap(protocolMap),
        );
        const i = this.templates.findIndex((t) => t.GUID === data.GUID);
        if (i === -1) {
          this.templates.push(data);
        } else {
          this.templates[i] = data;
        }
      } catch (err) {
        console.error(err);
      }
    },
    async putProtocolMap(protocolMap) {
      try {
        const { data } = await api.put(
          `api/v2/protocols/protocolmaps/${protocolMap.GUID}`,
          prepProtocolMap(protocolMap),
        );
        const i = this.templates.findIndex((t) => t.GUID === data.GUID);
        if (i === -1) {
          this.templates.push(data);
        } else {
          this.templates[i] = data;
        }
      } catch (err) {
        console.error(err);
      }
    },
    async deleteProtocolMap(protocolMapGUID) {
      try {
        await api.delete(`api / v2 / protocols / protocolmaps /${protocolMapGUID}`);
        this.templates = this.templates.filter((t) => t.GUID !== protocolMapGUID);
      } catch (err) {
        console.error(err);
      }
    },
    async fetchNotificationTemplates() {
      try {
        const res = await api.get(`api/v2/workflows/notifications`);
        return res.data;
      } catch (e) {
        console.error(e);
      }
    },
  },
});
/*
 * Then in the component, change the import to:
import { useMedicationStore } from '../stores/medication'
 * Use the new Vue3 setup method to connect to the store:
   setup () {
    const medicationStore = useMedicationStore()
    return { medicationStore }
  },
 * Change your use of the store from
 *
 this.$store.dispatch(MEDICATION_SAVE, this.localMedication)
 * to
 this.medicationStore.saveMedication(this.localMedication)
 */
